import { useLayoutEffect, useRef } from 'react';

/**
 * Apply dynamic values as CSS custom properties on an element via ref.
 *
 * Why not just `style={{ '--foo': value }}`? CSP `style-src-attr` blocks
 * inline `style="..."` attributes. Per spec, individual `CSSStyleDeclaration`
 * setters (`el.style.setProperty(...)`) are exempt from style-src-attr in
 * Chrome and Firefox, so this lets us use dynamic styling without
 * `'unsafe-inline'`. The rendered class (`w-[var(--foo)]`, etc.) lives in
 * external CSS, which is governed by the (already strict) `style-src`.
 *
 * Pass values as fully-qualified CSS strings (`"180px"`, `"#3b82f6"`,
 * `"50%"`). The hook does no unit coercion.
 */
export function useCssVars<T extends HTMLElement = HTMLDivElement>(
  vars: Record<string, string | null | undefined>,
) {
  const ref = useRef<T | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    for (const [name, value] of Object.entries(vars)) {
      if (value == null) el.style.removeProperty(name);
      else el.style.setProperty(name, value);
    }
  });
  return ref;
}
